/**
 * ============================================================
 *  FedeWorld Mod API
 * ============================================================
 *
 * A simple API that lets you add custom blocks, items, recipes,
 * and react to game events with hooks.
 *
 * How to use it from main.js (one time):
 *   import { ModAPI } from './api/ModAPI.js';
 *   ModAPI.init(player, world, hud, inventory, health);
 *
 * Then fire events from the game code:
 *   ModAPI.fireEvent('onBlockBreak', x, y, z, blockId);
 *   ModAPI.fireEvent('onBlockPlace', x, y, z, blockId);
 *   ModAPI.fireEvent('onMobKill', mobType, x, y, z);
 *   ModAPI.fireEvent('onChat', message);
 *   ModAPI.update(dt);  // fires onTick every frame
 *
 * ============================================================
 *  Example mod:
 * ============================================================
 *
 *   // Register a custom block
 *   ModAPI.registerBlock({
 *     id: 100,
 *     name: 'Ruby Block',
 *     texture: 10,    // tile index in the atlas
 *     hardness: 5
 *   });
 *
 *   // Register a custom item
 *   ModAPI.registerItem({
 *     id: 200,
 *     name: 'Ruby',
 *     stackSize: 64,
 *     type: 'material'
 *   });
 *
 *   // Register a crafting recipe (3x3 grid, 0 = empty)
 *   ModAPI.registerRecipe({
 *     grid: [
 *       200, 200, 200,
 *       200, 200, 200,
 *       200, 200, 200,
 *     ],
 *     result: { id: 100, count: 1 }
 *   });
 *
 *   // React when a block is broken
 *   ModAPI.onBlockBreak((x, y, z, blockId) => {
 *     console.log('Broke block', blockId, 'at', x, y, z);
 *   });
 *
 *   // Do something every frame
 *   ModAPI.onTick((dt) => {
 *     // dt = seconds since last frame
 *   });
 *
 *   // Show a message on screen
 *   ModAPI.sendMessage('Hello FedeWorld!');
 *
 *   // Read player info
 *   const info = ModAPI.getPlayer();
 *   console.log(info.x, info.y, info.z, info.health, info.hunger);
 *
 *   // Read / write blocks in the world
 *   const world = ModAPI.getWorld();
 *   const blockId = world.getBlock(10, 64, 10);
 *   world.setBlock(10, 65, 10, 3); // place stone
 *
 * ============================================================
 */

class _ModAPI {
  constructor() {
    // ---- Internal references (set by init) ----
    this._playerRef = null;
    this._worldRef = null;
    this._hudRef = null;
    this._inventoryRef = null;
    this._healthRef = null;

    // ---- Registered content ----
    this._customBlocks = [];
    this._customRecipes = [];
    this._customItems = [];

    // ---- Event hooks ----
    // Each key is an event name, value is an array of callbacks.
    this._hooks = {
      onBlockBreak: [],
      onBlockPlace: [],
      onMobKill: [],
      onTick: [],
      onChat: [],
    };
  }

  // ===========================================================
  //  Initialization  (called once from main.js)
  // ===========================================================

  /**
   * Wire up game references so the API can read player info,
   * access the world, and show messages on the HUD.
   *
   * @param {Player}    player    - the Player instance
   * @param {World}     world     - the World instance
   * @param {HUD}       hud       - the HUD instance
   * @param {Inventory} inventory - the Inventory instance
   * @param {Health}    health    - the Health instance
   */
  init(player, world, hud, inventory, health) {
    this._playerRef = player;
    this._worldRef = world;
    this._hudRef = hud;
    this._inventoryRef = inventory;
    this._healthRef = health;
  }

  // ===========================================================
  //  Register content
  // ===========================================================

  /**
   * Register a custom block.
   *
   * @param {object} block
   * @param {number} block.id       - unique block ID (use 100+ to avoid conflicts)
   * @param {string} block.name     - display name, e.g. 'Ruby Block'
   * @param {number} block.texture  - tile index in the texture atlas
   * @param {number} block.hardness - how long it takes to break (1 = normal)
   */
  registerBlock({ id, name, texture, hardness }) {
    if (id === undefined || !name) {
      console.warn('[ModAPI] registerBlock: id and name are required.');
      return;
    }
    const block = {
      id: id,
      name: name,
      texture: texture !== undefined ? texture : 0,
      hardness: hardness !== undefined ? hardness : 1,
    };
    this._customBlocks.push(block);
    console.log('[ModAPI] Block registered:', name, '(id ' + id + ')');
    return block;
  }

  /**
   * Register a crafting recipe.
   *
   * @param {object}   recipe
   * @param {number[]} recipe.grid   - flat array of 9 IDs (3x3), use 0 for empty
   * @param {object}   recipe.result - { id, count }
   */
  registerRecipe({ grid, result }) {
    if (!grid || !result) {
      console.warn('[ModAPI] registerRecipe: grid and result are required.');
      return;
    }
    const recipe = {
      grid: grid,
      result: result,
    };
    this._customRecipes.push(recipe);
    console.log('[ModAPI] Recipe registered for item', result.id);
    return recipe;
  }

  /**
   * Register a custom item.
   *
   * @param {object} item
   * @param {number} item.id        - unique item ID (use 200+ to avoid conflicts)
   * @param {string} item.name      - display name, e.g. 'Ruby'
   * @param {number} item.stackSize - max stack size (default 64)
   * @param {string} item.type      - 'material', 'tool', 'food', etc.
   */
  registerItem({ id, name, stackSize, type }) {
    if (id === undefined || !name) {
      console.warn('[ModAPI] registerItem: id and name are required.');
      return;
    }
    const item = {
      id: id,
      name: name,
      stackSize: stackSize !== undefined ? stackSize : 64,
      type: type || 'material',
    };
    this._customItems.push(item);
    console.log('[ModAPI] Item registered:', name, '(id ' + id + ')');
    return item;
  }

  // ===========================================================
  //  Event hooks
  // ===========================================================

  /**
   * Called when a block is broken.
   * Callback receives: (x, y, z, blockId)
   */
  onBlockBreak(callback) {
    this._hooks.onBlockBreak.push(callback);
  }

  /**
   * Called when a block is placed.
   * Callback receives: (x, y, z, blockId)
   */
  onBlockPlace(callback) {
    this._hooks.onBlockPlace.push(callback);
  }

  /**
   * Called when a mob is killed.
   * Callback receives: (mobType, x, y, z)
   */
  onMobKill(callback) {
    this._hooks.onMobKill.push(callback);
  }

  /**
   * Called every frame.
   * Callback receives: (dt) where dt is seconds since last frame.
   */
  onTick(callback) {
    this._hooks.onTick.push(callback);
  }

  /**
   * Called when a chat / terminal message is sent.
   * Callback receives: (message)
   */
  onChat(callback) {
    this._hooks.onChat.push(callback);
  }

  // ===========================================================
  //  Extended API (Fase 12)
  // ===========================================================

  /**
   * Register a custom entity type.
   * @param {{ id: string, name: string, health: number, speed: number, hostile: boolean, drops: Array }} config
   */
  registerEntity(config) {
    if (!config.id || !config.name) {
      console.warn('[ModAPI] registerEntity: id and name are required.');
      return;
    }
    if (!this._customEntities) this._customEntities = [];
    this._customEntities.push(config);
    console.log(`[ModAPI] Entity registered: ${config.name} (${config.id})`);
  }

  /**
   * Register a custom enchantment.
   * @param {{ id: string, name: string, maxLevel: number, appliesTo: string[] }} config
   */
  registerEnchantment(config) {
    if (!config.id || !config.name) return;
    if (!this._customEnchants) this._customEnchants = [];
    this._customEnchants.push(config);
    console.log(`[ModAPI] Enchantment registered: ${config.name}`);
  }

  /**
   * Register a custom potion effect.
   * @param {{ id: string, name: string, duration: number, color: string }} config
   */
  registerPotionEffect(config) {
    if (!config.id || !config.name) return;
    if (!this._customPotions) this._customPotions = [];
    this._customPotions.push(config);
    console.log(`[ModAPI] Potion effect registered: ${config.name}`);
  }

  /**
   * Display a message on the player's HUD.
   * @param {string} text
   * @param {number} [duration=3] seconds
   */
  showMessage(text, duration) {
    if (this._hudRef && this._hudRef.showMessage) {
      this._hudRef.showMessage(text, duration || 3);
    }
  }

  /**
   * Give an item to the player.
   * @param {number} itemId - negative for items, positive for blocks
   * @param {number} count
   */
  giveItem(itemId, count = 1) {
    if (!this._inventoryRef) return;
    if (itemId > 0) {
      this._inventoryRef.addBlock(itemId, count);
    } else {
      this._inventoryRef.addItem(itemId, count);
    }
  }

  /**
   * Teleport the player.
   * @param {number} x
   * @param {number} y
   * @param {number} z
   */
  teleport(x, y, z) {
    if (!this._playerRef) return;
    this._playerRef.position.set(x, y, z);
    if (this._playerRef.prevPosition) this._playerRef.prevPosition.copy(this._playerRef.position);
    if (this._playerRef.velocity) this._playerRef.velocity.set(0, 0, 0);
    if (this._worldRef && this._worldRef.updateLoadedChunks) {
      this._worldRef.updateLoadedChunks(this._playerRef.position);
    }
  }

  /**
   * Get all registered custom content for inspection.
   */
  getCustomContent() {
    return {
      blocks: this._customBlocks,
      items: this._customItems,
      recipes: this._customRecipes,
      entities: this._customEntities || [],
      enchantments: this._customEnchants || [],
      potions: this._customPotions || [],
    };
  }

  // ===========================================================
  //  Fire events  (called by the game engine)
  // ===========================================================

  /**
   * Fire all callbacks registered for the given event name.
   *
   * @param {string} eventName - 'onBlockBreak', 'onBlockPlace', etc.
   * @param {...*}   args      - arguments to pass to each callback
   */
  fireEvent(eventName, ...args) {
    const list = this._hooks[eventName];
    if (!list) return;
    for (let i = 0; i < list.length; i++) {
      try {
        list[i](...args);
      } catch (err) {
        console.error('[ModAPI] Error in ' + eventName + ' hook:', err);
      }
    }
  }

  /**
   * Called every frame by the game loop.
   * Fires all onTick hooks.
   *
   * @param {number} dt - seconds since last frame
   */
  update(dt) {
    this.fireEvent('onTick', dt);
  }

  // ===========================================================
  //  Game accessors
  // ===========================================================

  /**
   * Get current player info.
   *
   * @returns {object} { x, y, z, flying, inWater, health, maxHealth, hunger, maxHunger }
   */
  getPlayer() {
    const p = this._playerRef;
    const h = this._healthRef;
    if (!p) {
      console.warn('[ModAPI] getPlayer: not initialized yet. Call init() first.');
      return null;
    }
    return {
      x: p.position.x,
      y: p.position.y,
      z: p.position.z,
      flying: p.flying,
      inWater: p.inWater,
      health: h ? h.health : 20,
      maxHealth: h ? h.maxHealth : 20,
      hunger: h ? h.hunger : 20,
      maxHunger: h ? h.maxHunger : 20,
    };
  }

  /**
   * Get a safe world accessor for reading and writing blocks.
   *
   * @returns {object} { getBlock(x,y,z), setBlock(x,y,z,id) }
   */
  getWorld() {
    const w = this._worldRef;
    if (!w) {
      console.warn('[ModAPI] getWorld: not initialized yet. Call init() first.');
      return null;
    }
    return {
      /**
       * Get the block ID at world coordinates.
       * @param {number} x
       * @param {number} y
       * @param {number} z
       * @returns {number} block ID (0 = air)
       */
      getBlock(x, y, z) {
        return w.getBlock(x, y, z);
      },
      /**
       * Place a block at world coordinates.
       * @param {number} x
       * @param {number} y
       * @param {number} z
       * @param {number} id - block ID to place
       */
      setBlock(x, y, z, id) {
        w.setBlock(x, y, z, id);
      },
    };
  }

  /**
   * Show a message on the HUD (appears on screen for a few seconds).
   *
   * @param {string} text     - message to show
   * @param {number} duration - seconds to show (default 3)
   */
  sendMessage(text, duration) {
    if (this._hudRef && this._hudRef.showMessage) {
      this._hudRef.showMessage(text, duration !== undefined ? duration : 3);
    } else {
      console.log('[ModAPI] Message:', text);
    }
  }

  // ===========================================================
  //  Lookup helpers
  // ===========================================================

  /**
   * Find a registered custom block by its ID.
   * @param {number} id
   * @returns {object|null}
   */
  getBlockById(id) {
    for (let i = 0; i < this._customBlocks.length; i++) {
      if (this._customBlocks[i].id === id) return this._customBlocks[i];
    }
    return null;
  }

  /**
   * Find a registered custom item by its ID.
   * @param {number} id
   * @returns {object|null}
   */
  getItemById(id) {
    for (let i = 0; i < this._customItems.length; i++) {
      if (this._customItems[i].id === id) return this._customItems[i];
    }
    return null;
  }

  /**
   * Get all registered custom blocks.
   * @returns {object[]}
   */
  getCustomBlocks() {
    return this._customBlocks;
  }

  /**
   * Get all registered custom items.
   * @returns {object[]}
   */
  getCustomItems() {
    return this._customItems;
  }

  /**
   * Get all registered custom recipes.
   * @returns {object[]}
   */
  getCustomRecipes() {
    return this._customRecipes;
  }

  /**
   * Remove all hooks for a specific event. Useful for mod reloading.
   * @param {string} eventName
   */
  clearHooks(eventName) {
    if (this._hooks[eventName]) {
      this._hooks[eventName] = [];
    }
  }

  /**
   * Remove ALL hooks, blocks, items, and recipes. Full reset.
   */
  resetAll() {
    for (const key in this._hooks) {
      this._hooks[key] = [];
    }
    this._customBlocks = [];
    this._customRecipes = [];
    this._customItems = [];
    console.log('[ModAPI] All mods reset.');
  }
}

// Singleton - one ModAPI for the whole game
export const ModAPI = new _ModAPI();

// Also expose globally so mods loaded via <script> tags can access it
if (typeof window !== 'undefined') {
  window.ModAPI = ModAPI;
}
